import React, { useEffect, useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from './ui/dialog';
import { Button } from './ui/button';
import { AlertCircle } from 'lucide-react';
import api from '../lib/api';

const CACHE_KEY = 'subscription_cache';
const CACHE_TTL_MS = 5 * 60 * 1000; // 5 minutes

function getCachedSubscription() {
  try {
    const raw = sessionStorage.getItem(CACHE_KEY);
    if (!raw) return null;
    const cached = JSON.parse(raw);
    if (Date.now() - cached.ts > CACHE_TTL_MS) {
      sessionStorage.removeItem(CACHE_KEY);
      return null;
    }
    return cached.data;
  } catch {
    return null;
  }
}

function setCachedSubscription(data) {
  try {
    sessionStorage.setItem(CACHE_KEY, JSON.stringify({ data, ts: Date.now() }));
  } catch { /* ignore */ }
}

export const SubscriptionGuard = ({ children, requiredModule }) => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [showUpgradeModal, setShowUpgradeModal] = useState(false);
  const [blockReason, setBlockReason] = useState('');
  const checkedRef = useRef(false);

  useEffect(() => {
    if (checkedRef.current) return;
    checkedRef.current = true;

    // Team users inherit subscription from admin — no check needed
    if (user?.module_permissions) return;
    // Governor bypasses subscription
    if (user?.role === 'governor') return;

    // Check cache first
    const cached = getCachedSubscription();
    if (cached) {
      evaluateSubscription(cached);
      return;
    }

    // Fetch in background — never block rendering
    api.get('/api/subscriptions/current', { timeout: 3000 })
      .then(res => {
        const data = res.data;
        if (data) {
          setCachedSubscription(data);
          evaluateSubscription(data);
        }
      })
      .catch(() => {
        // On error or timeout, allow access (fail-open)
      });
  }, []);

  const evaluateSubscription = (data) => {
    if (data.status === 'expired') {
      setBlockReason('Your subscription has expired. Subscribe to continue using the application.');
      setShowUpgradeModal(true);
      return;
    }

    if (data.status === 'active' && requiredModule) {
      const features = data.features || {};
      if (features.modules === 1) {
        const selectedModule = data.selected_module;
        if (requiredModule !== selectedModule) {
          setBlockReason(`Your Starter plan only includes access to the ${selectedModule} module. Upgrade to access more modules.`);
          setShowUpgradeModal(true);
        }
      }
    }
  };

  if (showUpgradeModal) {
    return (
      <Dialog open={showUpgradeModal} onOpenChange={() => {}}>
        <DialogContent className="sm:max-w-md" data-testid="upgrade-required-modal">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <AlertCircle className="h-5 w-5 text-orange-500" />
              Upgrade Required
            </DialogTitle>
            <DialogDescription className="pt-2">{blockReason}</DialogDescription>
          </DialogHeader>
          <DialogFooter className="flex gap-2 sm:gap-0">
            <Button variant="outline" onClick={() => navigate('/dashboard')} data-testid="dismiss-button">
              Go to Dashboard
            </Button>
            <Button onClick={() => navigate('/billing')} data-testid="upgrade-button">
              View Plans
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    );
  }

  return <>{children}</>;
};
